<template>
  <div
    class="
      tw-border
      tw-bg-sf-snow-lighter
      tw-border-sf-neutral-lighter-2
      tw-shadow-sm
      tw-rounded-lg
    "
    data-testid="past-meeting-card"
  >
    <header
      class="
        tw-flex
        tw-h-[60px]
        tw-items-center
        tw-justify-between
        tw-bg-sf-snow-light
        tw-rounded-t-lg
        tw-px-6
      "
      :class="{
        'tw-rounded-b-lg': !meeting.summary,
      }"
    >
      <div class="tw-flex tw-items-center tw-gap-4">
        <span
          class="tw-flex tw-items-center tw-cursor-pointer tw-w-8 tw-h-8"
          @click="isExpanded = !isExpanded"
        >
          <i
            :class="isExpanded ? 'fa-chevron-down' : 'fa-chevron-right'"
            class="fa tw-text-sf-neutral-light-2 tw-mx-auto"
          />
        </span>
        <span
          v-if="meeting.title"
          class="tw-font-medium tw-text-base tw-text-sf-neutral-dark-2"
        >
          {{ meeting.title }}
        </span>
        <span class="tw-flex tw-items-center tw-gap-2">
          <i class="fa fa-calendar tw-text-sf-neutral-light-2" />
          <span class="tw-text-sm tw-text-sf-neutral-dark-1">
            {{ meeting.completedAt }}
          </span>
        </span>
        <span class="tw-font-medium tw-text-sm tw-text-sf-neutral-darker-1">
          {{ formatMessage(meeting.order.length) }}
        </span>
      </div>
      <div class="tw-flex tw-items-center tw-gap-4">
        <div
          class="
            tw-flex
            tw-items-center
            tw-rounded-lg
            tw-border
            tw-bg-sf-neutral-lighter-1
            tw-border-sf-neutral-subtle-2
            tw-gap-2
            tw-p-1.5
            tw-pl-2
          "
        >
          <span class="tw-text-sm tw-text-sf-gray">
            {{ I18n.t('client_meetings.past_meetings.agenda') }}
          </span>
          <SfButtonInverted variant="dark" small>
            {{ I18n.t('client_meetings.exports.generate_btn') }}
          </SfButtonInverted>
        </div>
        <span
          class="tw-flex tw-items-center tw-cursor-pointer tw-w-8 tw-h-8"
          data-testid="past-meeting-edit-button"
          @click="openMeetingModal(meeting)"
        >
          <i class="fa fa-pen tw-text-sf-neutral-light-2 tw-mx-auto"></i>
        </span>
      </div>
    </header>
    <div
      v-if="isExpanded || meeting.summary"
      class="tw-flex tw-flex-col tw-gap-4 tw-px-8 tw-py-4"
    >
      <div
        v-if="meeting.summary"
        class="
          tw-bg-sf-snow
          tw-rounded-lg
          tw-p-6
          tw-pt-4
          tw-text-sf-neutral-darker-1
          tw-flex
          tw-flex-col
          tw-gap-4
        "
      >
        <span class="tw-text-sm tw-font-medium">
          {{ I18n.t('client_meetings.past_meetings.meeting_summary') }}
        </span>
        <span class="tw-whitespace-pre-wrap">
          {{ meeting.summary }}
        </span>
      </div>

      <div v-if="isExpanded" class="tw-flex tw-flex-col tw-gap-4">
        <template v-if="loading">
          <RemarkV2Skeleton v-for="i in 2" :key="i" />
        </template>
        <div
          v-else-if="empty"
          class="tw-text-center tw-text-sf-neutral-dark-2 tw-text-sm"
        >
          {{ I18n.t('client_meetings.past_meetings.no_messages') }}
        </div>
        <template v-else>
          <RemarkV2
            v-for="remark in remarks"
            :key="remark.id"
            :remark="remark"
            part-of="past_meetings"
            show-context
            show-context-btn
            show-period
            hide-read-status
            show-tags
          />
        </template>
      </div>
    </div>
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'
import { defineProps } from 'vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { useStore } from 'vuex'
import RemarkV2Skeleton from '@app/shared/vue/components/molecules/remark-v2/remark-v2-skeleton.vue'
import RemarkV2 from '@app/shared/vue/components/molecules/remark-v2/remark-v2.vue'
import { SfButtonInverted } from '@app/shared/vue/components/sf-button'

const store = useStore()
const I18n = useI18n()

const props = defineProps({
  meeting: {
    type: Object,
    required: true,
  },
})

const emit = defineEmits(['open-meeting-modal'])

const isExpanded = ref(false)

const remarks = computed(() =>
  props.meeting.order.map((id) => store.state.remarks.remarks[id])
)

const empty = computed(() => remarks.value.length === 0)

const loading = computed(
  () => empty.value && store.getters.fetchingCommunicationsInProgress
)

function formatMessage(count) {
  const msg = I18n.t('client_meetings.past_meetings.message')
  const msgs = I18n.t('client_meetings.past_meetings.messages')

  return count === 1 ? count + ` ${msg}` : count + ` ${msgs}`
}

function openMeetingModal(meeting) {
  emit('open-meeting-modal', meeting)
}
</script>
